<script setup>
import { computed, ref, watch } from 'vue'
import { api, asList } from '../api/client'
import { historyActionLabels, historyFieldLabels, historyValue, isPriceHistoryEntry } from '../api/booking-history'
import { bookingStatusLabels } from '../api/mappers'
import { formatDate } from '../utils'

const props = defineProps({ bookingId: { type: [Number, String], required: true }, refreshKey: { type: Number, default: 0 } })
const entries = ref([])
const loading = ref(false)
const error = ref('')
const priceOnly = ref(false)
const visibleEntries = computed(() => priceOnly.value ? entries.value.filter(isPriceHistoryEntry) : entries.value)
let sequence = 0

async function load() {
  const requestSequence = ++sequence
  loading.value = true
  error.value = ''
  try {
    const rows = asList(await api.bookings.history(props.bookingId))
    if (requestSequence === sequence) entries.value = [...rows].reverse()
  } catch (requestError) {
    if (requestSequence === sequence) error.value = requestError.message || 'Не удалось загрузить историю.'
  } finally {
    if (requestSequence === sequence) loading.value = false
  }
}

function valueLabel(field, value) {
  if (['starts_at', 'ends_at', 'cancelled_at'].includes(field) && value) {
    return formatDate(value, { year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  if (field === 'status' && value) return bookingStatusLabels[value] || value
  return historyValue(field, value)
}

watch(() => [props.bookingId, props.refreshKey], load, { immediate: true })
</script>

<template>
  <section class="booking-history" aria-labelledby="booking-history-heading">
    <header class="section-header">
      <div>
        <h3 id="booking-history-heading">История изменений</h3>
        <p>Кто, когда и что изменил. Историю нельзя редактировать или удалять.</p>
      </div>
      <button type="button" class="button button--ghost" :disabled="loading" @click="load">Обновить историю</button>
    </header>
    <div class="history-filters" role="group" aria-label="Фильтр истории бронирования">
      <button type="button" class="button button--ghost" :aria-pressed="!priceOnly" @click="priceOnly = false">Все изменения</button>
      <button type="button" class="button button--ghost" :aria-pressed="priceOnly" @click="priceOnly = true">Цена и оплата</button>
    </div>
    <p v-if="loading" role="status">Загружаем историю…</p>
    <p v-else-if="error" class="form-error" role="alert">{{ error }}</p>
    <p v-else-if="!visibleEntries.length" class="history-empty">{{ priceOnly ? 'Изменений стоимости пока нет.' : 'Изменений пока нет. Для старой брони начальное состояние появится после применения миграции.' }}</p>
    <ol v-else class="history-list">
      <li v-for="entry in visibleEntries" :key="entry.id" class="history-entry">
        <div class="history-entry__header">
          <strong>{{ historyActionLabels[entry.action] || entry.action }}</strong>
          <time>{{ formatDate(entry.created_at, { year: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</time>
        </div>
        <p class="history-entry__actor">{{ entry.actor?.full_name || entry.actor?.username || 'Система / начальная фиксация' }}</p>
        <p v-if="entry.reason" class="history-entry__reason">{{ entry.reason }}</p>
        <dl class="history-changes">
          <div v-for="(change, field) in entry.changes" :key="field">
            <dt>{{ historyFieldLabels[field] || field }}</dt>
            <dd><span>{{ valueLabel(field, change.old) }}</span><span aria-label="изменено на"> → </span><strong>{{ valueLabel(field, change.new) }}</strong></dd>
          </div>
        </dl>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.booking-history { padding-top: 24px; border-top: 1px solid var(--line); }
.booking-history .section-header { align-items: flex-start; gap: 12px; }
.booking-history h3 { margin: 0 0 6px; }
.booking-history .section-header p, .history-empty { color: var(--muted); font-size: .78rem; line-height: 1.5; }
.history-list { list-style: none; padding: 0; margin: 16px 0 0; display: grid; gap: 12px; }
.history-filters { display: flex; gap: 6px; margin: 12px 0; flex-wrap: wrap; }
.history-filters [aria-pressed="true"] { background: var(--forest-pale); color: var(--forest); }
.history-entry { padding: 16px; border: 1px solid var(--line); border-radius: 10px; background: var(--paper-strong); }
.history-entry__header { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; font-size: .82rem; }
.history-entry__header time, .history-entry__actor { color: var(--muted); font-size: .75rem; }
.history-entry__actor { margin: 7px 0; }
.history-entry__reason { font-size: .8rem; white-space: pre-wrap; overflow-wrap: anywhere; }
.history-changes { margin: 10px 0 0; font-size: .75rem; }
.history-changes > div { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(0, 2fr); gap: 12px; padding: 6px 0; border-top: 1px solid var(--line); }
.history-changes dt { color: var(--muted); }
.history-changes dd { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 540px) { .history-changes > div { grid-template-columns: 1fr; gap: 4px; } .booking-history .section-header { flex-direction: column; } }
</style>
